import { getTier, getTrialDaysRemaining } from './license';

/**
 * Free tier daily usage tracker.
 * Limits free-tier users — anonymous web, signed-in free, and post-trial
 * desktop — to 3 tasks per day (resets daily). Active trial and paid tiers are
 * never capped. See usageCapApplies() for the gate.
 *
 * Canonical source: api/lib/billing/capabilities.js `daily_tool_limit`. Keep
 * this value in sync with that server-side constant.
 */
export const DAILY_LIMIT = 3;
const STORAGE_KEY = 'scrivar_web_tasks';

export function getUsageToday() {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const today = new Date().toISOString().split('T')[0];
    if (data.date !== today) return 0;
    return data.count || 0;
  } catch { return 0; }
}

export function incrementUsage() {
  const today = new Date().toISOString().split('T')[0];
  const current = getUsageToday();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: today, count: current + 1 }));
  return current + 1;
}

export function hasReachedLimit() {
  // Signed-in account users bypass when their tier includes paid features.
  try {
    const session = JSON.parse(localStorage.getItem('scrivar_session') || 'null');
    if (session?.tier && session.tier !== 'free') return false;
  } catch { /* ignore parse errors */ }
  return getUsageToday() >= DAILY_LIMIT;
}

/**
 * Is the free-tier daily task cap in force right now?
 *
 *   - Web: any non-paid user is capped (anonymous + signed-in free).
 *   - Desktop: only the post-trial Free state is capped. An ACTIVE trial is Pro
 *     (no cap); paid tiers are never capped. This is what makes an EXPIRED
 *     desktop trial drop to genuine Free per trial-onboarding spec §D.
 *
 * Replaces the bare isWebMode() guard at every tool call site.
 */
export function usageCapApplies() {
  const tier = getTier();
  if (tier === 'pro' || tier === 'lite') return false; // paid (lifetime normalizes to these)
  if (tier === 'trial') {
    // null start = unknown (boot window before /trial/status, or cleared
    // storage) → treat as active, not expired (matches hasFeature /
    // shouldWatermarkExport / TrialBanner, which all read null as non-expired).
    const days = getTrialDaysRemaining();
    return days !== null && days <= 0; // active = no cap; expired = cap
  }
  return true; // 'free'
}

export function isWebMode() {
  return !window.electronAPI?.isDesktop;
}
